import { useMemo } from 'react'
import type { Workout } from '../types/database.tables'
import { formatDateForComparison } from '../utils/dateUtils'

// Helper function for formatting recovery hours
const formatRecoveryHours = (hours: number): string => {
  if (hours < 24) {
    return `${Math.ceil(hours)} hour${hours > 1 ? 's' : ''}`
  } else {
    const days = Math.floor(hours / 24)
    const remainingHours = Math.round(hours % 24)
    if (remainingHours === 0) {
      return `${days} day${days > 1 ? 's' : ''}`
    }
    return `${days}d ${remainingHours}h`
  }
}

export const useWorkoutRecovery = () => {
  const calculateWorkoutRecoveryNeed = useMemo(() => {
    return (workout: Workout): number => {
      const baseRecovery = {
        swim: 8,
        bike: 12,
        run: 24,
        strength: 24,
        recovery: 0,
        brick: 20,
      }

      const intensityFactor = 1.0

      // TODO: consider intensity of workout

      // Duration factor (longer workouts need more recovery)
      const durationFactor = Math.max(0.5, workout.duration_minutes ?? 0 / 60) // normalized to 1 hour
      const base = baseRecovery[workout.activity_type as keyof typeof baseRecovery] || 12
      const adjustedDurationFactor = Math.min(durationFactor, 1.2)

      return base * intensityFactor * adjustedDurationFactor
    }
  }, [])

  const calculateWorkoutRecoveryData = useMemo(() => {
    return (currentWorkout: Workout, currentDate: string, allWorkouts: Workout[]): { level: number; hours: number } => {
      const currentWorkoutDate = new Date(currentDate)
      if (currentWorkout.time) {
        const [hours, minutes] = currentWorkout.time.split(':')
        currentWorkoutDate.setHours(parseInt(hours))
        currentWorkoutDate.setMinutes(parseInt(minutes))
      }

      let totalRecoveryNeed = 0

      const filteredWorkouts = []

      // find all previous workouts within 7 days
      for (let i = 1; i <= 7; i++) {
        // Start from 1 to exclude current day
        const checkDate = new Date(currentWorkoutDate)
        checkDate.setDate(currentWorkoutDate.getDate() - i)
        const checkDateString = formatDateForComparison(checkDate)
        const previousWorkouts = allWorkouts.filter(
          (w) => w.scheduled_date === checkDateString && w.id !== currentWorkout.id
        )
        filteredWorkouts.push(...previousWorkouts)
      }

      // Also include recovery from earlier workouts on the same day
      const parseTime = (time?: string) => {
        if (!time) {
          return 0
        }
        const [hh, mm] = time.split(':')
        return parseInt(hh) * 3600 + parseInt(mm) * 60
      }
      const sameDayWorkouts = allWorkouts.filter(
        (w) =>
          w.scheduled_date === currentDate &&
          parseTime(w.time ?? '') < parseTime(currentWorkout.time ?? '') &&
          w.id !== currentWorkout.id
      )
      filteredWorkouts.push(...sameDayWorkouts)

      filteredWorkouts.forEach((workout) => {
        const recoveryNeed = calculateWorkoutRecoveryNeed(workout)

        // Calculate hours elapsed
        const workoutDate = new Date(workout.scheduled_date)
        if (workout.time) {
          const [hours, minutes] = workout.time.split(':')
          workoutDate.setHours(parseInt(hours))
          workoutDate.setMinutes(parseInt(minutes))
        }
        let hoursElapsed = (currentWorkoutDate.getTime() - workoutDate.getTime()) / (1000 * 60 * 60)
        hoursElapsed = Math.max(0.5, hoursElapsed)
        const currentRecoveryNeed = Math.max(0, recoveryNeed - hoursElapsed)

        totalRecoveryNeed += Math.max(0, currentRecoveryNeed)
      })

      return {
        hours: totalRecoveryNeed,
        level: Math.min(1, totalRecoveryNeed / 8), // full width indicates, at least 8h recovery needed
      }
    }
  }, [calculateWorkoutRecoveryNeed])

  return {
    calculateWorkoutRecoveryNeed,
    calculateWorkoutRecoveryData,
    formatRecoveryHours,
  }
}
